/**
 * SN-8202. How long an account keeps accepting a player's USA Hockey number from the previous
 * season.
 *
 * USA Hockey issues next season's numbers from April 1 while last season's stay valid until
 * they lapse on September 1, so from April to August two seasons are simultaneously valid.
 * Associations differ on how much of that overlap they honour.
 *
 * The API stores the LAST ACCEPTED DAY as 'MM-DD' and accepts any date from 03-31 to 08-31;
 * `null` means USA Hockey's own allowance. The three presets below are what the settings card
 * offers today -- a free date picker can be added later without an API change.
 */

/** Sent to the API when the account takes USA Hockey's full allowance. */
export const HOCKEY_USA_FEDERATION_MAXIMUM = 'federation'

export type HockeyUSASeasonCutoffChoice = string

export const hockeyUSASeasonCutoffOptions = [
  {
    value: '03-31',
    label: 'Current season only (rejected from April 1)',
  },
  {
    value: '06-30',
    label: 'Through June 30 (most associations)',
  },
  {
    value: HOCKEY_USA_FEDERATION_MAXIMUM,
    label: "Through August 31 (USA Hockey's full allowance)",
  },
]

/**
 * API value -> the option the card should show selected.
 *
 * Null has never been set, and an explicit 08-31 behaves identically to it, so both land on
 * the federation-maximum option rather than leaving the select blank.
 */
export const toHockeyUSASeasonChoice = (
  cutoff?: string | null
): HockeyUSASeasonCutoffChoice => {
  if (!cutoff || cutoff === '08-31') {
    return HOCKEY_USA_FEDERATION_MAXIMUM
  }
  return cutoff
}

/** The option the admin picked -> what the API stores. */
export const fromHockeyUSASeasonChoice = (
  choice: HockeyUSASeasonCutoffChoice
): string | null => (choice === HOCKEY_USA_FEDERATION_MAXIMUM ? null : choice)
